import React from 'react';

const Section5VowBot = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-purple-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">🤖</span>
      <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
        AI Agent System: VowBot
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">A real agent with autonomous reasoning, not a chatbot wrapper</p>

    {/* Agent loop diagram */}
    <div className="bg-gradient-to-br from-purple-50/50 to-violet-50/50 rounded-2xl p-6 border border-purple-200/30 mb-6">
      <h3 className="text-base font-bold text-purple-600 uppercase tracking-wider mb-5 text-center">ReAct Agent Loop (Reasoning + Acting)</h3>
      <div className="flex flex-col items-center">
        <div className="flex flex-wrap md:flex-nowrap items-center justify-center gap-2">
          {[
            { step: 'User Query', icon: '💬', desc: 'Natural language input' },
            { step: 'Reasoning', icon: '🧠', desc: 'GPT-4o-mini decides intent' },
            { step: 'Tool Selection', icon: '🔧', desc: 'Pick 0-N tools to call' },
            { step: 'Execution', icon: '⚡', desc: 'Run tools in parallel' },
            { step: 'Synthesis', icon: '✨', desc: 'Combine results into response' },
          ].map((s, i) => (
            <React.Fragment key={i}>
              <div className="bg-white rounded-xl px-4 py-3 shadow-md border border-purple-200/50 text-center min-w-[120px] hover:scale-105 transition-transform duration-300">
                <span className="text-xl block">{s.icon}</span>
                <p className="text-sm font-bold text-gray-700 mt-1">{s.step}</p>
                <p className="text-xs text-gray-400">{s.desc}</p>
              </div>
              {i < 4 && <span className="text-purple-300 font-bold hidden md:block">→</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 text-gray-400">
          <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-purple-300"></div>
          <span className="text-xs font-bold text-purple-500">Max 3 iterations to prevent infinite loops</span>
          <div className="w-12 h-0.5 bg-gradient-to-r from-purple-300 to-transparent"></div>
        </div>
      </div>
    </div>

    {/* 7 Agent tools */}
    <div className="bg-gradient-to-br from-purple-50/50 to-violet-50/50 rounded-2xl p-6 border border-purple-200/30 mb-6">
      <h3 className="text-base font-bold text-purple-600 uppercase tracking-wider mb-5 text-center">7 Specialized Agent Tools</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { tool: 'Vendor Search', icon: '🏪', desc: 'Search 250+ vendors by category, location, budget. Smart location handling. Abbreviations like LA won\'t match Portland.', source: 'Database' },
          { tool: 'Budget Breakdown', icon: '💰', desc: 'Budget allocation recommendations by category. Optional total budget for dollar amounts.', source: 'Database' },
          { tool: 'Planning Timeline', icon: '📅', desc: 'Planning tasks with deadlines. Intelligent date parsing. Infers year if missing. Urgency categorization for short timelines.', source: 'Database' },
          { tool: 'Theme Search', icon: '🎨', desc: 'RAG semantic search across 200+ theme embeddings. Returns theme name, description, similarity score.', source: 'RAG' },
          { tool: 'Cultural Rituals', icon: '📿', desc: 'RAG search across 105 rituals. Personalized for logged-in users based on saved themes. Generic for guests.', source: 'RAG' },
          { tool: 'Model Performance', icon: '📊', desc: 'AI model quality comparison: scores, save rates, response times. Uses 5-min in-memory cache to avoid repeated DB hits.', source: 'Analytics' },
          { tool: 'Wedding Context', icon: '👤', desc: 'User\'s wedding details, theme count, vendor count. Auth-gated: only available to logged-in users.', source: 'Database' },
        ].map((t, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-purple-100/50 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xl">{t.icon}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 font-medium">{t.source}</span>
            </div>
            <p className="text-sm font-bold font-mono text-purple-700 mb-1">{t.tool}</p>
            <p className="text-xs text-gray-500 leading-relaxed">{t.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Smart features */}
    <div className="bg-gradient-to-r from-purple-50/80 to-violet-50/80 rounded-2xl p-5 border border-purple-200/30">
      <h3 className="text-base font-bold text-purple-600 uppercase tracking-wider mb-3">🎯 Agent Intelligence</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Auth-gated tool access', a: 'Guest users get 6 tools (no personal data). Logged-in users get all 7. The agent never sees tools it can\'t use, which means cleaner reasoning.' },
          { q: 'Guest message limit', a: '20 messages for guests, then a friendly login prompt with action buttons. Logged-in users have unlimited history.' },
          { q: 'Tone-adaptive temperature', a: '5 tones available (friendly, professional, romantic, practical, luxury). Temperature adjusts per tone: romantic=0.8, practical=0.3, default=0.7.' },
          { q: 'Multilingual support', a: '10 languages supported: English, Spanish, French, German, Italian, Portuguese, Chinese, Japanese, Korean, Hindi. Language-specific greetings and responses.' },
          { q: 'Smart suggested actions', a: 'After tool execution, the system analyzes results and generates contextual buttons: "Try nearby cities" (0 results), "Compare side-by-side" (3+ results), "Try Compare Mode" (model queries).' },
          { q: 'Page redirect detection', a: 'AI response text is scanned for feature mentions (e.g., "Dream Wedding Generator"). Matching features auto-generate clickable navigation buttons.' },
          { q: 'Security boundaries', a: 'System prompt explicitly forbids revealing tool names, model names, or architecture. Persistent technical questions get a clear, honest boundary response.' },
          { q: 'Feature upsell pattern', a: 'After every tool call, the agent mentions the related VowAI feature. Vendor search → Vendor Shortlisting Assistant, theme search → Dream Wedding Generator, etc.' },
          { q: 'Conversation persistence', a: 'Messages saved to localStorage, synced across all open tabs via storage events. Logout clears history. Login/logout events trigger context reload.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-purple-100/50">
            <p className="text-sm font-bold text-purple-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Section5VowBot;
